import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Search,
  LogOut,
  UserPlus,
  Phone,
  Globe,
  MapPin,
  Settings,
  UserCircle,
  ArrowLeft,
  Loader2,
  Building2,
  Save,
  FolderPlus,
  Trash2,
  ChevronRight,
  Target,
  Users
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

// Italian regions
const ITALIAN_REGIONS = [
  "Abruzzo", "Basilicata", "Calabria", "Campania", "Emilia-Romagna",
  "Friuli-Venezia Giulia", "Lazio", "Liguria", "Lombardia", "Marche",
  "Molise", "Piemonte", "Puglia", "Sardegna", "Sicilia", "Toscana",
  "Trentino-Alto Adige", "Umbria", "Valle d'Aosta", "Veneto"
];

const LeadsPage = () => {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  
  // Search state
  const [region, setRegion] = useState("");
  const [city, setCity] = useState("");
  const [activity, setActivity] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  
  // Results state
  const [leads, setLeads] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  
  // Groups state
  const [groups, setGroups] = useState([]);
  const [isLoadingGroups, setIsLoadingGroups] = useState(true);
  
  // Save dialog state
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [groupName, setGroupName] = useState("");
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  
  // Load groups on mount
  useEffect(() => {
    loadGroups();
  }, []);
  
  const loadGroups = async () => {
    setIsLoadingGroups(true);
    try {
      const response = await axios.get(`${API}/lead-groups`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGroups(response.data.groups || []);
    } catch (error) {
      console.error("Error loading groups:", error);
    } finally {
      setIsLoadingGroups(false);
    }
  };
  
  const handleSearch = async () => {
    if (!region || !activity.trim()) {
      toast.warning("Seleziona una regione e inserisci un'attività da cercare");
      return;
    }
    
    setIsSearching(true);
    setHasSearched(true);
    setSelectedLeads([]);
    
    try {
      const response = await axios.post(
        `${API}/leads/search`,
        {
          region,
          city: city.trim(),
          activity: activity.trim(),
          max_results: 60
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setLeads(response.data.leads || []);
      setSearchQuery(response.data.query || "");
      
      if (response.data.leads?.length === 0) {
        toast.info("Nessuna attività trovata");
      } else {
        toast.success(`Trovate ${response.data.leads.length} attività`);
      }
    } catch (error) {
      console.error("Search error:", error);
      const errorMsg = error.response?.data?.detail || "Errore durante la ricerca";
      toast.error(errorMsg);
    } finally {
      setIsSearching(false);
    }
  };
  
  const handleToggleSelectLead = (lead) => {
    setSelectedLeads(prev => {
      const isSelected = prev.some(l => l.place_id === lead.place_id);
      if (isSelected) {
        return prev.filter(l => l.place_id !== lead.place_id);
      } else {
        return [...prev, lead];
      }
    });
  };
  
  const handleSelectAll = () => {
    if (selectedLeads.length === leads.length) {
      setSelectedLeads([]);
    } else {
      setSelectedLeads([...leads]);
    }
  };
  
  const handleOpenSaveDialog = () => {
    if (selectedLeads.length === 0) {
      toast.warning("Seleziona almeno un lead da salvare");
      return;
    }
    
    // Suggest group name based on search
    const suggestedName = city 
      ? `${activity} - ${city}`
      : `${activity} - ${region}`;
    setGroupName(suggestedName);
    setSaveDialogOpen(true);
  };
  
  const handleSaveGroup = async () => {
    if (!groupName.trim()) {
      toast.warning("Inserisci un nome per il gruppo");
      return;
    }
    
    setIsSaving(true);
    
    try {
      // Create group
      const groupResponse = await axios.post(`${API}/lead-groups`, {
        name: groupName.trim(),
        search_query: searchQuery
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const groupId = groupResponse.data.group_id;
      
      // Add leads to group
      await axios.post(`${API}/lead-groups/${groupId}/add-leads`, selectedLeads, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Gruppo "${groupName}" creato con ${selectedLeads.length} lead`);
      setSaveDialogOpen(false);
      setSelectedLeads([]);
      setGroupName("");
      loadGroups();
      
    } catch (error) {
      console.error("Save error:", error);
      toast.error("Errore nel salvataggio del gruppo");
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleDeleteGroup = async (groupId, e) => {
    e.stopPropagation();
    
    if (!confirm("Eliminare questo gruppo e tutti i suoi lead?")) return;
    
    try {
      await axios.delete(`${API}/lead-groups/${groupId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Gruppo eliminato");
      loadGroups();
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Errore nell'eliminazione");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100" data-testid="leads-page">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b bg-white/80 backdrop-blur-md">
        <div className="container mx-auto px-4 flex h-16 items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/dashboard")}
              className="mr-2"
              data-testid="back-button"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Dashboard
            </Button>
            <div className="h-px w-6 bg-slate-300 rotate-90" />
            <div className="flex items-center gap-2">
              <div className="h-9 w-9 rounded-lg bg-orange-500 flex items-center justify-center">
                <UserPlus className="h-5 w-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold tracking-tight">Nuovi Lead</h1>
                <p className="text-xs text-muted-foreground hidden sm:block">Google My Business</p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="hidden sm:flex items-center gap-2 text-sm text-muted-foreground">
              <UserCircle className="h-4 w-4" />
              <span>{user?.name}</span>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={logout}
              data-testid="logout-button"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Esci
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Column - Search & Results */}
          <div className="lg:col-span-2 space-y-6">
            {/* Search Form */}
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Search className="h-5 w-5" />
                    Cerca Attività su Google My Business
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="space-y-2">
                      <Label>Regione *</Label>
                      <Select value={region} onValueChange={setRegion}>
                        <SelectTrigger data-testid="region-select">
                          <SelectValue placeholder="Seleziona" />
                        </SelectTrigger>
                        <SelectContent>
                          {ITALIAN_REGIONS.map((r) => (
                            <SelectItem key={r} value={r}>{r}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Città (opzionale)</Label>
                      <Input
                        placeholder="es: Milano"
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                        data-testid="city-input"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Attività *</Label>
                      <Input
                        placeholder="es: commercialisti"
                        value={activity}
                        onChange={(e) => setActivity(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                        data-testid="activity-input"
                      />
                    </div>
                    
                    <div className="flex items-end">
                      <Button
                        onClick={handleSearch}
                        disabled={isSearching}
                        className="w-full h-10 bg-orange-500 hover:bg-orange-600"
                        data-testid="search-button"
                      >
                        {isSearching ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <Search className="h-4 w-4 mr-2" />
                        )}
                        Cerca
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Search Results */}
            {hasSearched && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                      <CardTitle>Risultati ({leads.length})</CardTitle>
                      <CardDescription>{searchQuery}</CardDescription>
                    </div>
                    {leads.length > 0 && (
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleSelectAll}
                        >
                          {selectedLeads.length === leads.length ? "Deseleziona" : "Seleziona Tutti"}
                        </Button>
                        <Button
                          size="sm"
                          onClick={handleOpenSaveDialog}
                          disabled={selectedLeads.length === 0}
                          className="bg-orange-500 hover:bg-orange-600"
                        >
                          <FolderPlus className="h-4 w-4 mr-2" />
                          Salva ({selectedLeads.length})
                        </Button>
                      </div>
                    )}
                  </CardHeader>
                  <CardContent>
                    {leads.length === 0 ? (
                      <div className="text-center py-12 text-slate-500">
                        <Building2 className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                        <p>Nessuna attività trovata</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="w-10">
                                <input
                                  type="checkbox"
                                  checked={selectedLeads.length === leads.length}
                                  onChange={handleSelectAll}
                                  className="rounded"
                                />
                              </TableHead>
                              <TableHead>Nome / Indirizzo</TableHead>
                              <TableHead>Telefono</TableHead>
                              <TableHead>Sito Web</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {leads.map((lead, index) => (
                              <TableRow 
                                key={lead.place_id || index}
                                className={selectedLeads.some(l => l.place_id === lead.place_id) ? "bg-orange-50" : ""}
                              >
                                <TableCell>
                                  <input
                                    type="checkbox"
                                    checked={selectedLeads.some(l => l.place_id === lead.place_id)}
                                    onChange={() => handleToggleSelectLead(lead)}
                                    className="rounded"
                                  />
                                </TableCell>
                                <TableCell>
                                  <div className="flex flex-col">
                                    <span className="font-semibold text-slate-800">{lead.name}</span>
                                    {lead.address && (
                                      <span className="text-xs text-slate-500 flex items-center gap-1 mt-1">
                                        <MapPin className="h-3 w-3" />
                                        {lead.address}
                                      </span>
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell>
                                  {lead.phone ? (
                                    <a href={`tel:${lead.phone}`} className="text-blue-600 hover:underline flex items-center gap-1">
                                      <Phone className="h-3 w-3" />
                                      {lead.phone}
                                    </a>
                                  ) : (
                                    <span className="text-slate-400">-</span>
                                  )}
                                </TableCell>
                                <TableCell>
                                  {lead.website ? (
                                    <a 
                                      href={lead.website.startsWith('http') ? lead.website : `https://${lead.website}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-blue-600 hover:underline flex items-center gap-1"
                                    >
                                      <Globe className="h-3 w-3" />
                                      <span className="truncate max-w-[120px]">
                                        {lead.website.replace(/^https?:\/\//, '')}
                                      </span>
                                    </a>
                                  ) : (
                                    <span className="text-slate-400">-</span>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* Empty State */}
            {!hasSearched && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center py-16"
              >
                <div className="h-20 w-20 rounded-full bg-orange-100 flex items-center justify-center mx-auto mb-6">
                  <UserPlus className="h-10 w-10 text-orange-500" />
                </div>
                <h3 className="text-xl font-semibold text-slate-800 mb-2">
                  Cerca Nuovi Lead
                </h3>
                <p className="text-slate-600 max-w-md mx-auto">
                  Seleziona una regione, inserisci il tipo di attività e trova nuovi potenziali clienti su Google My Business.
                </p>
              </motion.div>
            )}
          </div>

          {/* Right Column - Saved Groups */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-5 w-5 text-orange-500" />
                  Gruppi Salvati
                </CardTitle>
                <CardDescription>
                  I tuoi gruppi di lead organizzati
                </CardDescription>
              </CardHeader>
              <CardContent>
                {isLoadingGroups ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                  </div>
                ) : groups.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Target className="h-10 w-10 mx-auto mb-3 text-slate-300" />
                    <p className="text-sm">Nessun gruppo salvato</p>
                    <p className="text-xs mt-1">Cerca e salva i lead per creare un gruppo</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {groups.map((group) => (
                      <div
                        key={group._id}
                        className="flex items-center justify-between p-3 rounded-lg border hover:bg-slate-50 cursor-pointer transition-colors group"
                        onClick={() => navigate(`/lead-group/detail?id=${group._id}`)}
                      >
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-lg bg-orange-100 flex items-center justify-center">
                            <Users className="h-5 w-5 text-orange-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-800">{group.name}</p>
                            <p className="text-xs text-slate-500">{group.leads_count} lead</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700"
                            onClick={(e) => handleDeleteGroup(group._id, e)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                          <ChevronRight className="h-4 w-4 text-slate-400" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      {/* Save Group Dialog */}
      <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Salva Lead in un Nuovo Gruppo</DialogTitle>
            <DialogDescription>
              Stai salvando {selectedLeads.length} lead. Inserisci un nome per il gruppo.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nome Gruppo</Label>
              <Input
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                placeholder="es: Commercialisti Milano"
                autoFocus
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveDialogOpen(false)}>
              Annulla
            </Button>
            <Button 
              onClick={handleSaveGroup}
              disabled={isSaving || !groupName.trim()}
              className="bg-orange-500 hover:bg-orange-600"
            >
              {isSaving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Salva Gruppo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LeadsPage;
